import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  fetchCompany,
  fetchInvitations,
  fetchMyCompany,
  resendInvitation,
  revealInvitationToken,
  revokeInvitation,
} from "../api/companyWorkspace";
import {
  canCopyOrRevokeInvitation,
  canManageInvitations,
  canReissueInvitation,
  invitationLandingPath,
  invitationStatusLabel,
  isOwnerMembership,
  roleLabel,
} from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
}

function CompanyInvitationsManageContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const tokenCacheRef = useRef({});
  const feedbackInvitationIdRef = useRef(null);
  const busyOpRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const clearTokenScope = useCallback(() => {
    tokenCacheRef.current = {};
    feedbackInvitationIdRef.current = null;
    setCopyFeedback(null);
  }, []);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusyId(null);
    }
  };

  const pruneTokenCache = useCallback(
    (companyData, membershipData, invitations) => {
      const list = Array.isArray(invitations) ? invitations : [];
      const keep = new Set();
      list.forEach((invitation) => {
        if (
          canCopyOrRevokeInvitation(companyData, membershipData, invitation)
        ) {
          keep.add(String(invitation.id));
        }
      });
      Object.keys(tokenCacheRef.current).forEach((id) => {
        if (!keep.has(String(id))) {
          delete tokenCacheRef.current[id];
        }
      });
      // Clear token-bearing feedback when its exact source invitation is gone
      // — an unrelated actionable invite must not preserve X's manual URL (R4-4).
      const feedbackId = feedbackInvitationIdRef.current;
      if (feedbackId != null && !keep.has(String(feedbackId))) {
        feedbackInvitationIdRef.current = null;
        setCopyFeedback(null);
      } else if (keep.size === 0) {
        feedbackInvitationIdRef.current = null;
        setCopyFeedback(null);
      }
    },
    [],
  );

  const load = useCallback(({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return Promise.resolve({ outcome: "not-found" });
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
    }
    return Promise.all([
      fetchCompany(companyId),
      fetchMyCompany(),
      fetchInvitations(companyId),
    ])
      .then(([companyData, mine, invitations]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return { outcome: "stale" };
        }
        const mineMatches =
          mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
            ? mine.membership
            : null;
        setCompany(companyData);
        setMembership(mineMatches);
        setAuthorityActorId(actorId);
        if (!canManageInvitations(companyData, mineMatches)) {
          setRows([]);
          clearTokenScope();
          setState("denied");
          return {
            outcome: "denied",
            company: companyData,
            membership: mineMatches,
            invitations: [],
          };
        }
        const list = Array.isArray(invitations) ? invitations : [];
        pruneTokenCache(companyData, mineMatches, list);
        setRows(list);
        revalidationConfirmed = true;
        setActionError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
        return {
          outcome: "ready",
          company: companyData,
          membership: mineMatches,
          invitations: list,
        };
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return { outcome: "stale" };
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return { outcome: "background-error" };
        }
        setRows([]);
        clearTokenScope();
        // Keep denied/error tied to the requesting actor — resolved 403 must
        // not sit behind awaiting-identity loading forever.
        setAuthorityActorId(actorId);
        if (status === 403) {
          setState("denied");
          return { outcome: "denied" };
        }
        if (status === 404) {
          setState("not-found");
          return { outcome: "not-found" };
        }
        setError(messageFromError(err));
        setState("error");
        return { outcome: "error" };
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, clearTokenScope, companyId, pruneTokenCache, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setBusyId(null);
    setActionError(null);
    clearTokenScope();
    setRows([]);
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
      clearTokenScope();
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const handleCopy = async (invitation) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    if (!canCopyOrRevokeInvitation(company, membership, invitation)) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: invitation.id,
    });
    busyOpRef.current = op;
    setBusyId(invitation.id);
    setActionError(null);
    setCopyFeedback(null);
    feedbackInvitationIdRef.current = null;
    try {
      // Every copy/re-copy must use fresh audited reveal — no cache shortcut.
      let token = null;
      try {
        const revealed = await revealInvitationToken(companyId, invitation.id);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        token = revealed?.token;
      } catch (err) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        delete tokenCacheRef.current[invitation.id];
        setCopyFeedback(null);
        const invStatus = err?.response?.data?.invitation_status;
        setActionError(
          invStatus
            ? `Запрошення більше не активне (${invitationStatusLabel(invStatus)}).`
            : messageFromError(err),
        );
        await load();
        return;
      }
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (!token) throw new Error("Токен недоступний");
      const link = `${origin}${invitationLandingPath(token)}`;
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      let ok = false;
      try {
        ok = await copyText(link);
      } catch {
        ok = false;
      }
      if (!scopeGate.lifetimeStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setCopyFeedback(
        ok
          ? "Посилання скопійовано."
          : `Не вдалося скопіювати автоматично. Скопіюйте вручну: ${link}`,
      );
      feedbackInvitationIdRef.current = invitation.id;
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setActionError(messageFromError(err));
    } finally {
      releaseBusy(op);
    }
  };

  const handleRevoke = async (invitation) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    if (!canCopyOrRevokeInvitation(company, membership, invitation)) return;
    const ok = window.confirm(
      `Відкликати запрошення для ${invitation.email} у «${company.name}»?`,
    );
    if (!ok) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: invitation.id,
    });
    busyOpRef.current = op;
    setBusyId(invitation.id);
    setActionError(null);
    try {
      await revokeInvitation(companyId, invitation.id);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      delete tokenCacheRef.current[invitation.id];
      setCopyFeedback(null);
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setActionError(messageFromError(err));
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  const handleResend = async (invitation) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    if (!canReissueInvitation(company, membership, invitation)) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: invitation.id,
    });
    busyOpRef.current = op;
    setBusyId(invitation.id);
    setActionError(null);
    try {
      const created = await resendInvitation(companyId, invitation.id);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      delete tokenCacheRef.current[invitation.id];
      const reload = await load();
      // Compare against LIVE lifetime — not captured closure ids.
      if (!scopeGate.lifetimeStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      // Denied/failed/superseded reload must not grant token cache/copy.
      if (!reload || reload.outcome !== "ready") {
        clearTokenScope();
        return;
      }
      const freshInvitation = (reload.invitations || []).find(
        (row) => created?.id != null && String(row.id) === String(created.id),
      );
      // Exact invitation + current actor/company authority — not generic list access.
      const mayExposeToken = Boolean(
        created?.token &&
          freshInvitation &&
          canCopyOrRevokeInvitation(
            reload.company,
            reload.membership,
            freshInvitation,
          ),
      );
      if (!mayExposeToken) {
        if (created?.id != null) {
          delete tokenCacheRef.current[created.id];
        }
        setCopyFeedback("Нове запрошення створено.");
        feedbackInvitationIdRef.current = created?.id ?? null;
        return;
      }
      if (!scopeGate.lifetimeStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const link = `${origin}${invitationLandingPath(created.token)}`;
      let copied = false;
      try {
        copied = await copyText(link);
      } catch {
        copied = false;
      }
      if (!scopeGate.lifetimeStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setCopyFeedback(
        copied
          ? "Нове запрошення створено; посилання скопійовано."
          : `Нове запрошення створено. Скопіюйте посилання вручну: ${link}`,
      );
      feedbackInvitationIdRef.current = created?.id ?? invitation.id;
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setActionError(messageFromError(err));
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;

  if (
    state === "loading" ||
    (state === "ready" && !authorityMatches)
  ) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження запрошень" />
      </div>
    );
  }

  if (state !== "ready") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title={
            state === "not-found"
              ? "Не знайдено"
              : state === "error"
                ? "Помилка"
                : "Немає доступу"
          }
          description={
            error ||
            "Керувати запрошеннями може лише активний власник або керівник активної компанії."
          }
          size="md"
        />
        {state === "error" ? (
          <button type="button" className="action-btn" onClick={load}>
            Повторити
          </button>
        ) : null}
      </div>
    );
  }

  if (!authorityMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження запрошень" />
      </div>
    );
  }

  const owner = isOwnerMembership(company, membership);

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container">
        <CompanyPageHeader
          title="Запрошення"
          subtitle="Список не містить посилань. Копіювання запитує свіжий токен у сервера."
          company={company}
          membership={membership}
          companyId={companyId}
          activeNav="invitations"
          actions={
            company.status === "active" ? (
              <Link
                to={`/companies/${companyId}/invitations/new`}
                className="btn-more"
              >
                Створити запрошення
              </Link>
            ) : null
          }
        />
        {copyFeedback ? (
          <p
            className="feature-company-workspace__note feature-company-workspace__long-value"
            role="status"
          >
            {copyFeedback}
          </p>
        ) : null}
        {actionError ? <InlineError>{actionError}</InlineError> : null}
        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-envelope"
            title="Немає запрошень"
            description="Створіть запрошення для рієлтора або керівника."
            size="md"
          />
        ) : (
          <ul className="feature-company-workspace__list">
            {rows.map((invitation) => {
              const live = canCopyOrRevokeInvitation(
                company,
                membership,
                invitation,
              );
              const reissue = canReissueInvitation(
                company,
                membership,
                invitation,
              );
              return (
                <li key={invitation.id} className="feature-company-workspace__card">
                  <div className="feature-company-workspace__card-top">
                    <div>
                      <h2 className="feature-company-workspace__long-value">
                        {invitation.email}
                      </h2>
                      <p>
                        {roleLabel(invitation.role)} ·{" "}
                        {invitationStatusLabel(invitation.status)}
                        {invitation.is_active_now === false &&
                        invitation.status === "pending"
                          ? " · строк минув"
                          : ""}
                        {!owner && invitation.role === "admin"
                          ? " (створено власником)"
                          : ""}
                      </p>
                    </div>
                    <div className="feature-company-workspace__inline-actions">
                      {live ? (
                        <button
                          type="button"
                          className="action-btn"
                          disabled={busyId === invitation.id}
                          onClick={() => handleCopy(invitation)}
                        >
                          Копіювати посилання
                        </button>
                      ) : null}
                      {live ? (
                        <button
                          type="button"
                          className="action-btn action-btn--danger"
                          disabled={busyId === invitation.id}
                          onClick={() => handleRevoke(invitation)}
                        >
                          Відкликати
                        </button>
                      ) : null}
                      {reissue ? (
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          disabled={busyId === invitation.id}
                          onClick={() => handleResend(invitation)}
                        >
                          Перевипустити
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CompanyInvitationsManageContent;
